/**
 * Чистая логика прав сотрудников на шаблон (без React), чтобы её можно было
 * покрыть unit-тестами и переиспользовать в EditTemplatePermissionsPopup.
 *
 * Ключевой инвариант (совпадает с бэком, см. funcs/employee_access_level.py
 * и check_template_permission): если роли НЕТ ключа в template.permissions —
 * действует кастомный профиль (access_profile), иначе access_level
 * (по умолчанию full: available / edit_template / edit_contract).
 * Уведомления (receive_notifications) — opt-in, кроме явного включения.
 */

export type PermissionsMap = { [roleId: string]: string[] };

export type EmployeeAccessLevel = 'full' | 'issue' | 'read';

export type TemplateScope = 'all' | 'selected';

export type AccessProfile = {
  _id: string;
  name: string;
  scope?: TemplateScope;
  default_permissions: string[];
  templates?: { [templateId: string]: string[] };
};

export type ActiveRoleForPermissions = {
  id: string;
  accessLevel?: string | null;
  accessProfile?: AccessProfile | null;
};

export const EMPLOYEE_ACCESS_LEVEL_OPTIONS: Array<{
  id: EmployeeAccessLevel;
  title: string;
  description: string;
}> = [
  {
    id: 'full',
    title: 'Полный доступ',
    description: 'На выданных шаблонах — выставление и правка. Новые шаблоны сами не появятся',
  },
  {
    id: 'issue',
    title: 'Только выставление',
    description: 'На выданных шаблонах — только выставление, без правки текста',
  },
  {
    id: 'read',
    title: 'Только чтение',
    description: 'На выданных шаблонах — только просмотр, без выставления и правок',
  },
];

export const TEMPLATE_PERMISSION_OPTIONS: Array<{
  id: string;
  label: string;
  description: string;
}> = [
  {
    id: 'available',
    label: 'Базовый доступ',
    description: 'Можно только выставлять договоры на подпись',
  },
  {
    id: 'edit_template',
    label: 'Редактирование шаблона',
    description: 'Возможность изменять текст шаблона и поля',
  },
  {
    id: 'edit_contract',
    label: 'Редактирование договора',
    description: 'Возможность редактировать договор выставленный на основе этого шаблона',
  },
  {
    id: 'receive_notifications',
    label: 'Получение уведомлений',
    description: 'Получение уведомлений о подписании договоров',
  },
];

/**
 * Права по умолчанию для уровня «полный доступ» / роли без access_level.
 * Полный доступ без уведомлений — как трактует отсутствие ключа бэкенд.
 */
export const DEFAULT_ROLE_TEMPLATE_PERMISSIONS: string[] = [
  'available',
  'edit_template',
  'edit_contract',
];

export function isBuiltinAccessLevel(value?: string | null): value is EmployeeAccessLevel {
  return value === 'full' || value === 'issue' || value === 'read';
}

export function normalizeEmployeeAccessLevel(
  value?: string | null,
): EmployeeAccessLevel {
  if (isBuiltinAccessLevel(value)) {
    return value;
  }
  return 'full';
}

export function accessLevelPermissions(
  level?: string | null,
): string[] {
  const normalized = normalizeEmployeeAccessLevel(level);
  if (normalized === 'issue') {
    return ['available'];
  }
  if (normalized === 'read') {
    return [];
  }
  return [...DEFAULT_ROLE_TEMPLATE_PERMISSIONS];
}

export function employeeAccessLevelTitle(
  level?: string | null,
  accessProfile?: AccessProfile | null,
): string {
  if (accessProfile?.name) {
    return accessProfile.name;
  }
  const normalized = normalizeEmployeeAccessLevel(level);
  return EMPLOYEE_ACCESS_LEVEL_OPTIONS.find((item) => item.id === normalized)?.title
    || EMPLOYEE_ACCESS_LEVEL_OPTIONS[0].title;
}

export function accessChoiceOptions(profiles: AccessProfile[] = []): Array<{
  id: string;
  title: string;
  description: string;
}> {
  return [
    ...EMPLOYEE_ACCESS_LEVEL_OPTIONS,
    ...profiles.map((profile) => ({
      id: String(profile._id),
      title: profile.name,
      description: 'Свои права по шаблонам',
    })),
  ];
}

export function roleAccessChoiceId(role: {
  access_level?: string | null;
  access_profile_id?: string | null;
}): string {
  if (role?.access_profile_id) {
    return String(role.access_profile_id);
  }
  return normalizeEmployeeAccessLevel(role?.access_level);
}

export function profilePermissionsForTemplate(
  profile: AccessProfile,
  templateId?: string | null,
): string[] {
  const overrides = profile.templates || {};
  if (profile.scope === 'selected') {
    if (templateId && Object.prototype.hasOwnProperty.call(overrides, templateId)) {
      return [...(overrides[templateId] || [])];
    }
    return [];
  }
  if (templateId && Object.prototype.hasOwnProperty.call(overrides, templateId)) {
    return [...(overrides[templateId] || [])];
  }
  return [...(profile.default_permissions || [])];
}

export function effectiveTemplatePermissions(
  role: ActiveRoleForPermissions,
  templateId?: string | null,
): string[] {
  if (role.accessProfile) {
    return profilePermissionsForTemplate(role.accessProfile, templateId);
  }
  return accessLevelPermissions(role.accessLevel);
}

function profileDefaultIncludes(userSession: any, permission: string): boolean {
  const defaults = userSession?.access_profile?.default_permissions;
  return Array.isArray(defaults) && defaults.indexOf(permission) !== -1;
}

function allowlistHasPermission(userSession: any, permission: string): boolean {
  const allowed = userSession?.allowed_templates;
  if (!allowed || typeof allowed !== 'object') {
    return false;
  }
  return Object.values(allowed).some(
    (perms) => Array.isArray(perms) && perms.indexOf(permission) !== -1,
  );
}

export function sessionCanCreateTemplates(userSession: any): boolean {
  if (!userSession?.role_id) {
    return true;
  }
  if (userSession.access_profile) {
    return profileDefaultIncludes(userSession, 'edit_template');
  }
  return normalizeEmployeeAccessLevel(userSession.access_level) === 'full';
}

export function sessionCanCreateContracts(userSession: any): boolean {
  if (!userSession?.role_id) {
    return true;
  }
  if (userSession.access_profile) {
    if (userSession.access_profile.scope === 'selected') {
      return allowlistHasPermission(
        {allowed_templates: userSession.access_profile.templates},
        'available',
      );
    }
    return profileDefaultIncludes(userSession, 'available');
  }
  if (userSession.template_scope === 'selected') {
    return allowlistHasPermission(userSession, 'available');
  }
  return normalizeEmployeeAccessLevel(userSession.access_level) !== 'read';
}

function samePermissionSet(left: string[] | undefined, right: string[]): boolean {
  const a = left || [];
  if (a.length !== right.length) {
    return false;
  }
  return a.every((item) => right.indexOf(item) !== -1)
    && right.every((item) => a.indexOf(item) !== -1);
}

function asActiveRoles(
  activeRoles: string[] | ActiveRoleForPermissions[],
): ActiveRoleForPermissions[] {
  return activeRoles.map((role) => (
    typeof role === 'string' ? { id: role, accessLevel: 'full' } : role
  ));
}

/**
 * Строит начальную карту прав для отображения в попапе.
 *
 * Для каждой активной роли без явной записи подставляем права её
 * access_level / профиля (иначе галочки выглядят «слетевшими»).
 * Явно сохранённые права, включая пустой список `[]` (доступ отозван),
 * сохраняются как есть. Незнакомые ключи тоже не теряются.
 */
export function computeInitialPermissions(
  storedPermissions: PermissionsMap | undefined | null,
  activeRoles: string[] | ActiveRoleForPermissions[],
  templateId?: string | null,
): PermissionsMap {
  const next: PermissionsMap = { ...(storedPermissions || {}) };
  for (const role of asActiveRoles(activeRoles)) {
    if (!Object.prototype.hasOwnProperty.call(next, role.id)) {
      next[role.id] = effectiveTemplatePermissions(role, templateId);
    }
  }
  return next;
}

/**
 * Перед сохранением убирает ключи, совпадающие с дефолтом access_level /
 * профиля, чтобы уровень оставался «живым» (в т.ч. для новых шаблонов).
 */
export function omitAccessLevelDefaults(
  permissions: PermissionsMap,
  activeRoles: ActiveRoleForPermissions[],
  templateId?: string | null,
): PermissionsMap {
  const next: PermissionsMap = { ...permissions };
  for (const role of activeRoles) {
    const expected = effectiveTemplatePermissions(role, templateId);
    if (samePermissionSet(next[role.id], expected)) {
      delete next[role.id];
    }
  }
  return next;
}

/**
 * Переключает одно право в наборе роли.
 *
 * - `available` — базовое право: снятие обнуляет весь набор, установка даёт
 *   ровно `['available']`.
 * - Любое другое право при установке автоматически добавляет `available`
 *   (без базового доступа остальные права бессмысленны).
 */
export function togglePermission(
  current: string[] | undefined,
  permissionId: string,
): string[] {
  const currentPermissions = current || [];

  if (permissionId === 'available') {
    return currentPermissions.includes('available') ? [] : ['available'];
  }

  if (currentPermissions.includes(permissionId)) {
    return currentPermissions.filter((p) => p !== permissionId);
  }

  const next = [...currentPermissions, permissionId];
  if (!currentPermissions.includes('available')) {
    next.push('available');
  }
  return next;
}
